import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import {
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import PaginationV2 from "../../../components/PaginationV2";
import BrandForm from "./BrandForm";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import EditableCell from "../../../components/EditableCell";
import useDialogStore from "../../../stores/useDialogStore";
import LoadingComponent from "../../../components/LoadingComponent";
import NoData from "../../../components/NoData";
import ColumnSearch from "../../../components/ColumnSearch";
import Button from "../../../components/Button";

export default function BrandList() {
  const user = getUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { openDialog, closeDialog } = useDialogStore();
  const searchParams = new URLSearchParams(location.search);
  const currentPage = parseInt(searchParams.get("page") || "1");

  // Sorting config
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });

  // Handle column search
  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
  };

  // Handle column sort
  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
      setSortConfig({ key: column, direction: newDirection });
    } else {
      // If sorting by Name, always start with ascending (A-Z)
      const initialDirection = column === "name" ? "desc" : direction;
      params.set("sortBy", column);
      params.set("order", initialDirection);
      setSortConfig({ key: column, direction: initialDirection });
    }
    navigate(`${location.pathname}?${params.toString()}`);
    setSortConfig({ key: column, direction });
  };

  // Fetch brands using React Query
  const {
    data: brandsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["brands", currentPage, location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      params.set("page", currentPage);
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-brands?${params.toString()}`
      );
      return response.data;
    },
  });

  // Delete brand mutation
  const deleteBrandMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(
        `${window.REACT_APP_API_ENDPOINT}/device-brands/${id}`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["brands"]);
      closeDialog();
      toast.success("Brand deleted successfully!");
    },
    onError: (error) => {
      toast.error("Failed to delete brand. Please try again.");
      console.error("Delete brand error:", error);
    },
  });

  // Edit brand mutation
  const editBrandMutation = useMutation({
    mutationFn: ({ brandId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/device-brands/${brandId}`,
        { [field]: value }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["brands"]);
      toast.success("Brand updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update brand. Please try again.");
      console.error("Update brand error:", error);
    },
  });

  // Update brand mutation for inline editing

  const handleEditBrand = (brand) => {
    openDialog({
      title: brand.id ? "Edit Brand" : "Add New Brand",
      content: (
        <BrandForm
          brandID={brand.id}
          updateData={() => {
            refetch();
            closeDialog();
          }}
        />
      ),
    });
  };

  const handlePageChange = (page) => {
    const params = new URLSearchParams(location.search);
    params.set("page", page);
    navigate(`${location.pathname}?${params.toString()}`);
  };
  const handleClearFilters = () => {
    navigate(location.pathname);
    queryClient.invalidateQueries(["brands"]);
  };

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
          </div>
        </div>
        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">Brands</h4>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<XMarkIcon className="h-4 w-4" />}
                    onClick={handleClearFilters}
                    className="mb-2 lg:mb-0"
                  >
                    Clear Filters
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditBrand({})}
                    className="mb-2 lg:mb-0"
                  >
                    + Add New Brand
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        ID
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Name"
                            columnKey="name"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Name..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        Logo
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Order"
                            columnKey="order"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Order..."
                          />
                        </div>
                      </th>

                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={5} className="text-center">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : brandsData?.data?.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center">
                          <NoData message="No brands found" />
                        </td>
                      </tr>
                    ) : (
                      brandsData?.data?.map((brand, index) => (
                        <tr
                          key={brand.id}
                          className={`divide-y divide-gray-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-gray-100"
                          }`}
                        >
                          <td className="whitespace-nowrap py-5 pl-8 text-sm ">
                            {brand.id}
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <EditableCell
                                value={brand.name}
                                onSave={(newValue) => {
                                  editBrandMutation.mutate({
                                    brandId: brand.id,
                                    field: "name",
                                    value: newValue.value,
                                  });
                                }}
                                inputType="text"
                                className="font-medium text-gray-900"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <img
                                src={
                                  window.REACT_APP_API_ENDPOINT.replace(
                                    /\/api(?!.*\/api)/,
                                    ""
                                  ) + brand.logo
                                }
                                alt="Brand Logo"
                                className="w-10 h-10 object-contain rounded-full"
                              />
                            </div>
                          </td>

                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <EditableCell
                                value={brand.order}
                                onSave={(newValue) => {
                                  editBrandMutation.mutate({
                                    brandId: brand.id,
                                    field: "order",
                                    value: parseInt(newValue.value),
                                  });
                                }}
                                inputType="number"
                                className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-900"
                              />
                            </div>
                          </td>

                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center gap-2 pl-4">
                              <Button
                                variant="outline-info"
                                size="icon"
                                icon={<PencilSquareIcon className="h-4 w-4" />}
                                onClick={() => handleEditBrand(brand)}
                                className="p-1"
                              />
                              {user.role === "SuperAdmin" && (
                                <Button
                                  variant="outline-danger"
                                  size="icon"
                                  icon={<TrashIcon className="h-4 w-4" />}
                                  onClick={() => {
                                    openDialog({
                                      title: "Delete Brand",
                                      content: (
                                        <div className="flex flex-col gap-4">
                                          <p className="text-lg">
                                            Are you sure you want to delete{" "}
                                            <span className="font-bold">
                                              {brand.name}
                                            </span>
                                            <br />
                                            <span className="text-red-500 text-sm font-bold">
                                              (This action will move the brand
                                              to trash)
                                            </span>
                                          </p>
                                          <div className="flex items-center justify-end gap-2">
                                            <Button
                                              variant="light"
                                              size="sm"
                                              onClick={() => closeDialog()}
                                            >
                                              Cancel
                                            </Button>
                                            <Button
                                              variant="danger"
                                              size="sm"
                                              icon={
                                                <TrashIcon className="h-4 w-4" />
                                              }
                                              onClick={() => {
                                                deleteBrandMutation.mutate(
                                                  brand.id
                                                );
                                              }}
                                            >
                                              Delete
                                            </Button>
                                          </div>
                                        </div>
                                      ),
                                    });
                                  }}
                                  className="p-1"
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={brandsData?.pagination?.current_page}
                last_page={brandsData?.pagination?.last_page}
                next_page_url={brandsData?.pagination?.next_page_url}
                prev_page_url={brandsData?.pagination?.prev_page_url}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
